import {
  Paper,
  SimpleGrid,
  Autocomplete,
  Select,
  Button,
  Loader,
} from '@mantine/core';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import { CodeableConceptInput } from '@/components/medplum-react/CodeableConceptInput/CodeableConceptInput';
import { ResourceInput } from '@medplum/react';
import {
  ResourceSearchFormLayout,
  SearchFieldDefinition,
} from './ResourceSearchForm.types';
import { createSearchUpdate } from '@/utils/search-utils';

interface ResourceSearchFormProps<TParams> {
  fields: (SearchFieldDefinition<TParams> | false | null | undefined)[];
  searchParams: TParams;
  onSearch: (params: Partial<TParams>) => void;
  onClear: () => void;
  clearLabel?: string;
  layout?: ResourceSearchFormLayout;
}

export function ResourceSearchForm<TParams>({
  fields,
  searchParams,
  onSearch,
  onClear,
  clearLabel = 'Clear',
  layout,
}: ResourceSearchFormProps<TParams>) {
  const resolvedFields = fields.filter(
    (field): field is SearchFieldDefinition<TParams> => Boolean(field)
  );

  const gridCols = layout?.cols ?? { base: 1, sm: 2, lg: 4 };
  const gridSpacing = layout?.spacing ?? 'md';

  return (
    <Paper shadow="sm" p="md" withBorder>
      <SimpleGrid cols={gridCols} spacing={gridSpacing}>
        {resolvedFields.map((field) => {
          if (field.show && !field.show(searchParams)) {
            return null;
          }

          const fallbackValue = searchParams[field.key];
          const fieldValue =
            field.value !== undefined ? field.value : fallbackValue;

          const resolveOptions = () => {
            if (typeof field.options === 'function') {
              return field.options();
            }
            return field.options ?? [];
          };

          const resolveLoading = () => {
            if (typeof field.loading === 'function') {
              return field.loading();
            }
            return field.loading ?? false;
          };

          switch (field.type) {
            case 'autocomplete':
              return (
                <Autocomplete
                  key={field.key}
                  placeholder={field.placeholder}
                  data={(resolveOptions() as string[]) || []}
                  value={(fieldValue as string) ?? ''}
                  clearable={field.clearable ?? true}
                  leftSection={field.icon}
                  rightSection={resolveLoading() ? <Loader size="xs" /> : null}
                  onChange={(val) => {
                    field.onQueryChange?.(val);
                    if (field.onChange) {
                      field.onChange(val);
                    } else if (!val) {
                      onSearch(
                        createSearchUpdate<TParams>(field.key, undefined)
                      );
                    }
                  }}
                  onOptionSubmit={(val) => {
                    if (field.onOptionSubmit) {
                      field.onOptionSubmit(val);
                    } else {
                      onSearch(createSearchUpdate<TParams>(field.key, val));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const next = e.currentTarget.value;
                      if (field.onEnter) {
                        field.onEnter(next);
                      } else {
                        onSearch(createSearchUpdate<TParams>(field.key, next));
                      }
                    }
                  }}
                />
              );

            case 'select':
              return (
                <Select
                  key={field.key}
                  placeholder={field.placeholder}
                  data={resolveOptions()}
                  clearable={field.clearable}
                  value={
                    fieldValue === undefined || fieldValue === ''
                      ? null
                      : (fieldValue as string)
                  }
                  onChange={(val) => {
                    if (field.onChange) {
                      field.onChange(val);
                    } else {
                      onSearch(
                        createSearchUpdate<TParams>(field.key, val || undefined)
                      );
                    }
                  }}
                />
              );

            case 'date':
              return (
                <DateInputWrapper
                  key={field.key}
                  placeholder={field.placeholder}
                  value={(fieldValue as Date | null | undefined) ?? null}
                  onChange={(val) => {
                    if (field.onChange) {
                      field.onChange(val);
                    } else {
                      onSearch(createSearchUpdate<TParams>(field.key, val));
                    }
                  }}
                />
              );

            case 'codeableConcept':
              return (
                <CodeableConceptInput
                  key={`${field.key}-${searchParams[field.key] || 'empty'}`}
                  path={field.key}
                  binding={field.binding}
                  name={`${field.key}-filter`}
                  placeholder={field.placeholder}
                  defaultValue={(field.defaultValue ?? fieldValue) || undefined}
                  maxValues={1}
                  clearable
                  loadAll
                  onChange={(val) => {
                    if (field.onChange) {
                      field.onChange(val);
                    } else {
                      onSearch(createSearchUpdate<TParams>(field.key, val));
                    }
                  }}
                />
              );

            case 'resource':
              return (
                <ResourceInput
                  key={`${field.key}-${(fieldValue as any)?.id || 'empty'}`}
                  resourceType={field.resourceType as any}
                  name={field.key}
                  placeholder={field.placeholder}
                  defaultValue={fieldValue as any}
                  onChange={(val) => {
                    if (field.onChange) {
                      field.onChange(val as any);
                    } else {
                      onSearch(
                        createSearchUpdate<TParams>(
                          field.key,
                          val?.id || undefined
                        )
                      );
                    }
                  }}
                />
              );

            default:
              return null;
          }
        })}

        <Button onClick={onClear} variant="outline">
          {clearLabel}
        </Button>
      </SimpleGrid>
    </Paper>
  );
}
